import React, {useMemo} from 'react';
import {List} from 'immutable';
import {GUIDE_TYPES} from 'client/constants';
import {SubjectModelV2, QuestionModelV3, QuestionSetModelV1} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';

import MarkdownBlock from '../MarkdownBlock/MarkdownBlock';
import {
  useQuestionTranslationStore,
  ExplanationText,
  SampleResponse as SampleResponseText
} from '../TranslationSection';
import {TagsList, StandardsList} from '../TagsStandardsList/TagsStandardsList.react';

import './content-blocks.scss';

interface PromptProps {
  question?: QuestionModelV3;
  transformText?: (text: string) => string;
}

export const Prompt = ({question, transformText}: PromptProps) => {
  const {translatedQuestionInfo, showPromptTranslation} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showPromptTranslation: state.showTranslations && state.translationSettings.question
    }))
  );

  const text = useMemo(() => {
    let promptText = '';
    if (showPromptTranslation && translatedQuestionInfo) {
      promptText = translatedQuestionInfo.prompt;
    } else {
      promptText = question.getPrompt();
    }
    if (transformText) {
      promptText = transformText(promptText);
    }
    return promptText;
  }, [question, showPromptTranslation, translatedQuestionInfo, transformText]);

  return <MarkdownBlock key={question?.getId()} text={text} />;
};

interface ExplainationProps {
  question?: QuestionModelV3;
}

export const Explanation = ({question}: ExplainationProps) => {
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const text = useMemo(() => {
    if (showTranslations && translatedQuestionInfo) {
      return translatedQuestionInfo.solution_text;
    }
    return question.getMeta().getSolutionText();
  }, [question, translatedQuestionInfo, showTranslations]);

  const headingText = useMemo(
    () => (showTranslations ? ExplanationText : 'Explanation'),
    [showTranslations]
  );

  return <MarkdownBlock heading={headingText} text={text} />;
};

interface SampleResponseProps {
  question?: QuestionModelV3;
}

export const SampleResponse = ({question}: SampleResponseProps) => {
  const {translatedQuestionInfo, showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      translatedQuestionInfo: state.translatedQuestionInfo,
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );
  const sample: string = useMemo(() => {
    if (showTranslations) {
      return translatedQuestionInfo?.sample;
    }
    return question.getMeta().getSample();
  }, [question, showTranslations, translatedQuestionInfo]);

  return (
    <>
      {sample && (
        <MarkdownBlock
          heading={showTranslations ? SampleResponseText : 'Sample Response'}
          text={sample}
        />
      )}
    </>
  );
};

interface StandardsProps {
  question?: QuestionModelV3;
  subject?: SubjectModelV2;
  questionSet?: QuestionSetModelV1;
}

export const Standards = ({question, subject, questionSet}: StandardsProps) => {
  const guideTypeLinkString = useMemo(() => {
    const guideType = questionSet.getGuideLevels().first()?.getGuide().getGuideType();
    const typeLinkString = guideType === GUIDE_TYPES.ASSESSMENT ? '/assessments' : '';
    return typeLinkString;
  }, [questionSet]);

  return (
    <StandardsList
      standards={question.getStandards()}
      linkToFunc={(item) =>
        `/${subject.getUrlSlug()}${guideTypeLinkString}?standards=${item.getId()}`
      }
    />
  );
};

interface TagsProps {
  question?: QuestionModelV3;
  subject?: SubjectModelV2;
  questionSet?: QuestionSetModelV1;
}

export const Tags = ({question, subject, questionSet}: TagsProps) => {
  const guideTypeLinkString = useMemo(() => {
    const guideType = questionSet.getGuideLevels().first()?.getGuide().getGuideType();
    const typeLinkString = guideType === GUIDE_TYPES.ASSESSMENT ? '/assessments' : '';
    return typeLinkString;
  }, [questionSet]);
  return (
    <TagsList
      tags={question.getQuestionSet().getTags ? question.getQuestionSet().getTags() : List()}
      linkToFunc={(item) => `/${subject.getUrlSlug()}${guideTypeLinkString}?tags=${item.getId()}`}
    />
  );
};
